import { useEffect } from 'react';
import { useInfiniteQuery, type UseInfiniteQueryResult } from 'react-query';
import { apiClient } from '../../services/ApiClient';
import { type Filters, type TripsPanelViewModel, type Page, Event, Feature } from '../../types';
import { convertFilterToQuery } from '../../utils/convert-filters-to-query';
import { useAppInsights } from '../useAppInsights';
import { getEventFactory } from '../../utils/get-event-factory';
import { useTripsContext } from '../../components/GlobalContextProvider/GlobalContextProvider';

import { defaultTripsPageNumber } from '../../components/TripsPanel/constants';
import { ApiEndpoints } from '../../constants/api-constants';
import { Queries, QueryCacheConfig } from '../../constants/query-constants';

export const useTripsInfiniteQuery = (
  filters: Filters,
  limit: number,
  currentPage: number,
  page: Page
): UseInfiniteQueryResult<TripsPanelViewModel> => {
  const queryString = convertFilterToQuery(filters);
  const { previousFilters } = useTripsContext();
  const appInsights = useAppInsights();
  const getTrips = async ({ pageParam: pageNumber = defaultTripsPageNumber }): Promise<TripsPanelViewModel> => {
    return apiClient.get<TripsPanelViewModel>(ApiEndpoints.trips.getTripsPaginated(pageNumber, limit, queryString));
  };

  const result = useInfiniteQuery<TripsPanelViewModel>({
    ...QueryCacheConfig[Queries.TripsPaginated],
    queryKey: [Queries.TripsPaginated, queryString, currentPage, limit],
    queryFn: getTrips,
    getNextPageParam: lastPage => {
      return lastPage?.paging?.hasNextPage ? lastPage.paging.page + 1 : null;
    },
    enabled: !!filters.port,
  });

  const hasAnyTrip = (): boolean => {
    const tripsLength = result?.data?.pages?.reduce((acc, val) => acc + (val?.trips ?? []).length, 0);
    return tripsLength > 0;
  };

  useEffect(() => {
    if (result?.isLoading || hasAnyTrip()) {
      return;
    }

    appInsights.trackAnalyticsEvent(
      getEventFactory(Event.NoTripsFound)(Feature.TripsHistoryPanel, page, previousFilters, filters)
    );
  }, [result?.data?.pages]);

  return result;
};
